import React,{useEffect,useRef,useState} from 'react';
import {ImageOff} from 'lucide-react';
import {useAuth} from '../services/authContext';
import {fetchPhotoBlob} from '../services/photoMedia';
import type {JournalPhoto} from '../services/journalRepository';

export default function CameraImage({photo,alt,className='',lazy=false}:{photo:Pick<JournalPhoto,'photo_id'|'image_url'|'publication_revision'>;alt:string;className?:string;lazy?:boolean}){
 const {user,idToken,privileges}=useAuth(),[wake,setWake]=useState(0);
 const placeholder=useRef<HTMLDivElement>(null),[nearby,setNearby]=useState(!lazy);
 useEffect(()=>{
  if(!lazy||nearby)return;
  if(typeof IntersectionObserver==='undefined'){setNearby(true);return;}
  const observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting)){setNearby(true);observer.disconnect();}},{rootMargin:'240px'});
  if(placeholder.current)observer.observe(placeholder.current);return()=>observer.disconnect();
 },[lazy,nearby]);
 const scope=JSON.stringify([user?.email,user?.status,user?.farm_ids,user?.roles,idToken,privileges,photo.photo_id,photo.publication_revision,wake]);
 const current=useRef(scope);current.current=scope;
 const [image,setImage]=useState<{scope:string;url?:string;failed?:boolean}>({scope:''});
 const imageState=useRef(image);imageState.current=image;
 useEffect(()=>{const reset=()=>setWake(value=>value+1);window.addEventListener('offline',reset);window.addEventListener('online',reset);
  document.addEventListener('visibilitychange',reset);window.addEventListener('sonata-photo-cache-purged',reset);
  const retryFailed=()=>{if(imageState.current.failed)reset();};window.addEventListener('focus',retryFailed);
  return()=>{window.removeEventListener('focus',retryFailed);window.removeEventListener('offline',reset);window.removeEventListener('online',reset);document.removeEventListener('visibilitychange',reset);window.removeEventListener('sonata-photo-cache-purged',reset);};},[]);
 useEffect(()=>{
  const abort=new AbortController();let url:string|undefined;setImage({scope});
  if((lazy&&!nearby)||!navigator.onLine||document.visibilityState==='hidden'||user?.status!=='Active'||!idToken)return()=>abort.abort();
  void fetchPhotoBlob(photo.image_url!,abort.signal,String(photo.publication_revision)).then(blob=>{
   if(abort.signal.aborted||current.current!==scope)return;
   url=URL.createObjectURL(blob);setImage({scope,url});
  }).catch(()=>{if(!abort.signal.aborted&&current.current===scope)setImage({scope,failed:true});});
  return()=>{abort.abort();if(url)URL.revokeObjectURL(url);};
 },[scope,photo.image_url,lazy,nearby]);
 const state=image.scope===scope?image:undefined;
 return state?.url?<img src={state.url} alt={alt} className={className} onError={()=>setImage({scope,failed:true})}/>:
  <div ref={placeholder} className={className+' flex flex-col items-center justify-center gap-2 bg-stone-100 text-stone-500'} role={state?.failed?'img':'status'} aria-label={state?.failed?alt+' — image unavailable':'Loading photo'}>
   {state?.failed&&<><ImageOff size={24}/><span className="text-xs">Image unavailable</span></>}
  </div>;
}
